import React, { useState, useEffect, useCallback } from 'react'
import { ArrowLeftRight, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react'
import { useCurrencyStore } from '@/stores/currencyStore'
import { ConversionResult } from '@/types/currency'
import { formatCurrency, formatPercentage, formatTimeAgo } from '@/utils/formatters'
import { POPULAR_CURRENCIES } from '@/utils/constants'
import CurrencySelector from './CurrencySelector'
import RateDisplay from './RateDisplay'
import ConversionHistory from './ConversionHistory'

const CurrencyConverter: React.FC = () => {
  const {
    convert,
    preferences,
    lastUpdate
  } = useCurrencyStore()

  const [fromCurrency, setFromCurrency] = useState('USD')
  const [toCurrency, setToCurrency] = useState('EUR')
  const [amount, setAmount] = useState('')
  const [result, setResult] = useState<ConversionResult | null>(null)
  const [isConverting, setIsConverting] = useState(false)
  const [currentRate, setCurrentRate] = useState<number | null>(null)
  const [rateChange, setRateChange] = useState<number | null>(null)

  // Instant conversion using cached rates
  const performConversion = useCallback(async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setResult(null)
      setCurrentRate(null)
      return
    }

    setIsConverting(true)
    try {
      const numAmount = parseFloat(amount)
      
      // This now uses cached rates for instant conversion
      const conversionResult = await convert(numAmount, fromCurrency, toCurrency)
      setResult(conversionResult)
      
      // Set rate from the conversion result for immediate display
      if (conversionResult) {
        setCurrentRate(conversionResult.exchangeRate)
        // Calculate rate change (mock for now - would need historical data)
        setRateChange(Math.random() * 2 - 1) // Random between -1 and 1
      }
    } catch (error) {
      console.error('Conversion error:', error)
      setResult(null)
      setCurrentRate(null)
    } finally {
      setIsConverting(false)
    }
  }, [amount, fromCurrency, toCurrency, convert])

  // Auto-convert when inputs change - now instant with cached rates
  useEffect(() => {
    const timeoutId = setTimeout(performConversion, 100) // Reduced to 100ms for instant feel
    return () => clearTimeout(timeoutId)
  }, [performConversion])

  // Swap currencies
  const swapCurrencies = () => {
    setFromCurrency(toCurrency)
    setToCurrency(fromCurrency)
  }



  // Handle amount input
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value)
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Header */}
      <div className="text-center space-y-1">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Currency Converter</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Real-time exchange rates from multiple providers
        </p>
        {lastUpdate && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Last updated: {formatTimeAgo(lastUpdate)}
          </p>
        )}
      </div>

      {/* Main Converter Card */}
      <div className="card p-4 space-y-4">
        {/* Amount Input */}
        <div className="space-y-1">
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Amount
          </label>
          <div className="relative">
            <input
              id="amount"
              type="text"
              value={amount}
              onChange={handleAmountChange}
              placeholder="Enter amount..."
              className="currency-input w-full p-3 text-center border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors dark:bg-gray-700"
              autoFocus
            />
            {isConverting && (
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            )}
          </div>
        </div>

        {/* Currency Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
          {/* From Currency */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">From</label>
            <CurrencySelector
              value={fromCurrency}
              onChange={setFromCurrency}
              placeholder="Select currency..."
            />
          </div>

          {/* Swap Button */}
          <div className="flex justify-center">
            <button
              onClick={swapCurrencies}
              className="p-3 rounded-full bg-primary-100 hover:bg-primary-200 dark:bg-primary-800 dark:hover:bg-primary-700 border-2 border-primary-200 dark:border-primary-600 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 shadow-sm"
              aria-label="Swap currencies"
            >
              <ArrowLeftRight className="w-5 h-5 text-primary-600 dark:text-primary-300" />
            </button>
          </div>

          {/* To Currency */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">To</label>
            <CurrencySelector
              value={toCurrency}
              onChange={setToCurrency}
              placeholder="Select currency..."
            />
          </div>
        </div>

        {/* Conversion Result */}
        {result && (
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-4 space-y-2">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-700 mb-1">
                {result.formattedAmount}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {formatCurrency(result.originalAmount, fromCurrency, preferences.decimalPrecision)} = {result.formattedAmount}
              </div>
            </div>

            {/* Rate Information */}
            {currentRate && (
              <div className="flex items-center justify-center space-x-4 text-xs">
                <div className="flex items-center space-x-1">
                  <span className="text-gray-600">Rate:</span>
                  <span className="font-mono font-medium">
                    1 {fromCurrency} = {currentRate.toFixed(4)} {toCurrency}
                  </span>
                </div>
                {rateChange !== null && (
                  <div className={`flex items-center space-x-1 ${
                    rateChange >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {rateChange >= 0 ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <TrendingDown className="w-4 h-4" />
                    )}
                    <span className="font-medium">
                      {formatPercentage(Math.abs(rateChange))}
                    </span>
                  </div>
                )}
              </div>
            )}


          </div>
        )}
      </div>

      {/* Quick Currency Buttons */}
      <div className="card p-3">
        <h3 className="text-base font-semibold mb-2">Popular Currencies</h3>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
          {POPULAR_CURRENCIES.slice(0, 8).map((currency) => (
            <button
              key={currency}
              onClick={() => {
                if (currency !== fromCurrency) {
                  setToCurrency(currency)
                }
              }}
              className={`p-2 text-sm font-medium rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                currency === toCurrency
                  ? 'bg-primary-100 border-primary-300 text-primary-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {currency}
            </button>
          ))}
        </div>
      </div>

      {/* Rate Display Component */}
      <RateDisplay fromCurrency={fromCurrency} toCurrency={toCurrency} />

      {/* Conversion History */}
      <ConversionHistory />
    </div>
  )
}

export default CurrencyConverter